import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router'
import { SingleDatePicker } from '@/components/commons/date-picker'
import { InputText } from '@/components/commons/input-text'
import { MarqueeBreadcrumbs } from '@/components/commons/marquee-breadcrumbs'
import { Radio } from '@/components/commons/radio'
import { Button } from '@/components/ui/button'
import { CalendarIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Label } from '@/components/ui/label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { dayjs } from '@/dayjs'
import {
	ComboboxField,
	type ComboboxOption
} from '@/features/marquee/components/combobox-field'
import {
	COLUMN_WIDTH,
	Field,
	RequiredLabel,
	useErrorText
} from '@/features/marquee/components/event-form-atoms'
import { EventFormCtasSection } from '@/features/marquee/components/event-form-ctas-section'
import { EventFormInfoCard } from '@/features/marquee/components/event-form-info-card'
import { EventFormInviteeSection } from '@/features/marquee/components/event-form-invitee-section'
import { TimeRangeFields } from '@/features/marquee/components/event-form-time-fields'
import {
	handleImportError,
	showImportSuccessToast
} from '@/features/marquee/components/import-toasts'
import { LocationDialog } from '@/features/marquee/components/location-dialog'
import { ManagerMultiSelect } from '@/features/marquee/components/manager-multi-select'
import {
	EventFormSchema,
	type EventFormValues
} from '@/features/marquee/form-schemas'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	locationsQueryOptions,
	marketManagersQueryOptions,
	marketsQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	MARQUEE_CAPTURE_MODE_OPTIONS,
	MARQUEE_DEFAULT_CAPTURE_MODE,
	MARQUEE_DEFAULT_EVENT_TYPE,
	MARQUEE_EVENT_FORMAT_OPTIONS,
	MARQUEE_EVENT_TYPE_OPTIONS
} from '@/features/marquee/types'
import {
	captureModeToHintKey,
	captureModeToLabelKey,
	ctaValidationMessage,
	eventFormatToLabel,
	eventPlaceValue,
	eventTypeToLabelKey,
	isMarketManagerAllowed,
	locationErrorCode,
	locationErrorLabelKey,
	locationPlaceLabel,
	parseEventPlace
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import { routes } from '@/routes'
import {
	createEvent,
	importInvitees,
	type MarqueeEventCreate,
	type MarqueeEventFormat,
	type MarqueeEventUpdate,
	updateEvent
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Blank form used by the create flow. */
const EMPTY_DEFAULTS: EventFormValues = {
	name: '',
	market: '',
	date: '',
	startTime: '',
	endTime: '',
	format: undefined,
	captureMode: MARQUEE_DEFAULT_CAPTURE_MODE,
	eventType: MARQUEE_DEFAULT_EVENT_TYPE,
	hashtag: '#',
	ctas: [],
	marketManagerIds: []
}

/**
 * A custom location the picker can resolve on submit: its parent market goes
 * out as `market_geo_id`, and its zone drives the times hint.
 */
export interface PickableLocation {
	id: string
	name: string
	marketId: string
	marketName: string
	timeZone: string
}

interface MarqueeEventFormProps {
	/** Prefilled values for the edit flow; omitted for the create flow. */
	initialValues?: EventFormValues
	/** When set, the form PATCHes this event instead of creating a new one. */
	editEventId?: string
	/**
	 * The event's resolved IANA zone (edit only). The backend derives it from
	 * the market, so a create has none to show until the event exists.
	 */
	timeZone?: string
	/**
	 * Edit only: the event's own custom location. Kept as an option even when
	 * it has been deactivated since, the way the event's market always is.
	 */
	currentLocation?: PickableLocation
}

function MarqueeEventForm({
	initialValues,
	editEventId,
	timeZone,
	currentLocation
}: MarqueeEventFormProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const isEdit = !!editEventId

	const { data: markets = [], isLoading: isLoadingMarkets } = useQuery(
		marketsQueryOptions()
	)
	// Loaded on its own and never awaited: the markets are enough to create an
	// event, and the locations join the list when they arrive.
	const { data: locations } = useQuery(locationsQueryOptions())
	const { data: managers = [], isLoading: isLoadingManagers } = useQuery(
		marketManagersQueryOptions()
	)
	const { data: operator } = useQuery(operatorQueryOptions())
	const showAddLocation = isMarketManagerAllowed(operator, 'createLocation')
	const [isAddingLocation, setIsAddingLocation] = useState(false)
	// Every popup on the form portals here, not to the body: the page is its own
	// stacking context, so they scroll under the sticky header. That header has
	// to stay under the modals, which rules out simply raising it above them.
	const pageRef = useRef<HTMLDivElement>(null)
	const queryClient = useQueryClient()
	// An edit can change the row as it appears in the tracker list (name, date,
	// market) as well as the detail, so both writes invalidate the shared
	// `events` prefix rather than the list and the detail separately.
	const invalidateEvents = () =>
		queryClient.invalidateQueries({
			queryKey: makeQueryKeys.marquee.events.all()
		})

	const create = useMutation({
		mutationFn: async (body: MarqueeEventCreate) => {
			const response = await createEvent({ body })
			return response.data
		},
		onSuccess: invalidateEvents
	})
	const update = useMutation({
		mutationFn: async (body: MarqueeEventUpdate) => {
			const response = await updateEvent({
				path: { eventId: editEventId ?? '' },
				body
			})
			return response.data
		},
		onSuccess: invalidateEvents
	})
	const importCsv = useMutation({
		mutationFn: async (variables: { eventId: string; file: File }) => {
			const response = await importInvitees({
				path: { eventId: variables.eventId },
				body: { file: variables.file }
			})
			return response.data
		},
		onSuccess: async (result) => {
			await invalidateEvents()
			showImportSuccessToast(result)
		}
	})
	// The invitee CSV is a File held outside the form (create-only) and validated
	// separately from the zod schema; `inviteeError` shows its required message.
	const [inviteeFile, setInviteeFile] = useState<File | null>(null)
	const [inviteeError, setInviteeError] = useState(false)
	const isSubmitting =
		create.isPending || importCsv.isPending || update.isPending

	/** The active locations, plus the edited event's own if it was deactivated. */
	const pickableLocations = useMemo<PickableLocation[]>(() => {
		const active = (locations?.items ?? []).map((location) => ({
			id: location.id,
			name: location.name,
			marketId: location.market.id,
			marketName: location.market.name,
			timeZone: location.time_zone
		}))
		if (
			currentLocation &&
			!active.some((location) => location.id === currentLocation.id)
		) {
			active.unshift(currentLocation)
		}
		return active
	}, [locations, currentLocation])

	/**
	 * One list, two kinds (OPSSTD-10100): the custom locations first, labelled
	 * with their parent market, then every open market. Since OPSSTD-10072 the
	 * backend accepts any market: one with no zone set in Marquee falls back to
	 * America/Los_Angeles and the event says so with `time_zone_defaulted`.
	 */
	const marketOptions = useMemo<ComboboxOption[]>(
		() => [
			...pickableLocations.map((location) => ({
				value: eventPlaceValue({ kind: 'location', id: location.id }),
				label: locationPlaceLabel(location.name, location.marketName)
			})),
			...markets.map((market) => ({
				value: eventPlaceValue({ kind: 'market', id: market.id }),
				label: market.name
			}))
		],
		[pickableLocations, markets]
	)
	const managerOptions = useMemo<ComboboxOption[]>(
		() =>
			managers.map((m) => ({
				value: m.id,
				label: m.name ?? m.email ?? ''
			})),
		[managers]
	)

	const {
		control,
		register,
		handleSubmit,
		setValue,
		setError,
		formState: { errors }
	} = useForm<EventFormValues>({
		resolver: zodResolver(EventFormSchema),
		defaultValues: initialValues ?? EMPTY_DEFAULTS
	})

	// Keep the "#" anchor in place if the user clears the hashtag entirely,
	// so they don't have to retype it themselves.
	const hashtagField = register('hashtag')
	const handleHashtagBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			hashtagField.onBlur(event)
			if (!event.target.value) {
				setValue('hashtag', '#')
			}
		},
		[hashtagField, setValue]
	)

	// Both modes carry a blanket selection; only the presentation requires one.
	// A tune-up also captures per attendee, so its blanket set may stay empty.
	const captureModeValue = useWatch({ control, name: 'captureMode' })
	const requiresCtas = captureModeValue === 'presentation'

	/**
	 * The `market_geo_id` / `location_id` pair a picked place sends. A market
	 * clears any location (`null`); a location sends its parent market as well,
	 * which the backend always requires and checks against it. `undefined` when
	 * a picked location is no longer resolvable (deactivated meanwhile).
	 */
	const placeFields = (value: string) => {
		const place = parseEventPlace(value)
		if (place?.kind === 'market') {
			return { market_geo_id: place.id, location_id: null }
		}
		const location =
			place && pickableLocations.find((l) => l.id === place.id)
		return location
			? { market_geo_id: location.marketId, location_id: location.id }
			: undefined
	}

	// The times hint follows the pick: a location's own zone, the event's zone
	// while the place is the one it was saved with, otherwise unknown (a market's
	// zone is only resolved once the event is saved).
	const placeValue = useWatch({ control, name: 'market' })
	const pickedPlace = parseEventPlace(placeValue)
	const pickedLocation =
		pickedPlace?.kind === 'location'
			? pickableLocations.find((l) => l.id === pickedPlace.id)
			: undefined
	const hintTimeZone =
		pickedLocation?.timeZone ??
		(placeValue === initialValues?.market ? timeZone : undefined)

	const handleInviteeChange = useCallback((file: File | null) => {
		setInviteeFile(file)
		if (file) {
			setInviteeError(false)
		}
	}, [])

	/**
	 * Routes a create/update failure to where the operator can act on it: the
	 * CTA field for the 422 that names it, a toast for everything else.
	 */
	const handleWriteError = (error: unknown) => {
		// The picked location was deactivated or no longer fits the market:
		// said on the field, where picking again fixes it.
		const locationCode = locationErrorCode(error)
		if (
			locationCode === 'location-not-found' ||
			locationCode === 'location-market-mismatch'
		) {
			setError('market', {
				type: 'server',
				message: locationErrorLabelKey(locationCode)
			})
			return
		}
		const ctaMessage = ctaValidationMessage(error)
		if (ctaMessage) {
			// A backend sentence, not an i18n key — `useErrorText` resolves it
			// through `t()`, which passes an unknown key through unchanged.
			setError('ctas', { type: 'server', message: ctaMessage })
			return
		}
		handleImportError(error)
	}

	/**
	 * How an empty selection travels, which is the one asymmetry in the field:
	 * only a tune-up can reach here empty (the schema holds a presentation to at
	 * least one code), and an edit says so with `[]`, which PATCH reads as
	 * "empty the selection". Create rejects `[]` 422 in both modes, so there the
	 * same intent is a field left out (`undefined`) instead.
	 */
	const blanketCtas = (selection: string[]): string[] | undefined => {
		if (selection.length > 0) {
			return selection
		}
		return isEdit ? [] : undefined
	}

	/**
	 * What stops a submit before any request, outside the zod schema: the
	 * invitee CSV (required on create only) and a place that still resolves.
	 * Returns the place fields, or `undefined` after flagging the problem.
	 */
	const preflight = (data: EventFormValues) => {
		if (!(isEdit || inviteeFile)) {
			setInviteeError(true)
			return undefined
		}
		const place = placeFields(data.market)
		if (!place) {
			setError('market', {
				type: 'server',
				message: locationErrorLabelKey('location-not-found')
			})
		}
		return place
	}

	const onSubmit = handleSubmit(async (data) => {
		const place = preflight(data)
		if (!place) {
			return
		}
		const ctas = blanketCtas(data.ctas)
		const shared = {
			name: data.name,
			...place,
			date: data.date,
			start_time: data.startTime,
			end_time: data.endTime || undefined,
			format: data.format || undefined,
			hashtag: data.hashtag,
			...(ctas && { ctas }),
			assigned_mms: data.marketManagerIds ?? []
		}
		try {
			let redirectId = editEventId ?? ''
			if (isEdit) {
				// `capture_mode` is immutable, so PATCH carries no trace of it.
				// `event_type` is nullable here: null is the contract's "not
				// recorded", so a legacy event keeps it until one is chosen.
				await update.mutateAsync({
					...shared,
					event_type: data.eventType
				})
			} else {
				// One user action, two calls: create the event, then import the
				// CSV against the returned id (POST /events → POST .../import).
				// The event already exists once creation succeeds, so we still
				// redirect to it even if the import call itself fails.
				const created = await create.mutateAsync({
					...shared,
					capture_mode: data.captureMode,
					event_type: data.eventType ?? undefined
				})
				redirectId = created.id
				if (inviteeFile) {
					try {
						await importCsv.mutateAsync({
							eventId: created.id,
							file: inviteeFile
						})
					} catch (importError) {
						handleImportError(importError)
					}
				}
			}
			navigate(
				generatePath(routes.MARQUEE_EVENT_DETAIL.handle.fullPath, {
					id: redirectId
				})
			)
		} catch (error) {
			handleWriteError(error)
		}
	})

	const errorText = useErrorText()

	return (
		<div className='isolate flex flex-col' ref={pageRef}>
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<MarqueeBreadcrumbs />
			</div>

			<div className='flex flex-col gap-1 l:px-10 px-4 py-4'>
				<h1 className='font-medium text-neutral-900 text-xl'>
					{t(isEdit ? 'marquee:edit_title' : 'marquee:create_title')}
				</h1>
				<p className='text-neutral-500 text-sm'>
					{t('marquee:create_subtitle')}
				</p>
			</div>

			<form
				className='flex xxl:flex-row flex-col xxl:items-start gap-10 l:px-10 px-4 pt-4 pb-16'
				onSubmit={onSubmit}
			>
				{/* Left column: form */}
				<div className='flex min-w-0 flex-1 flex-col gap-8'>
					{/* Event name */}
					<InputText
						errorMessage={errorText(errors.name?.message)}
						label={
							<RequiredLabel
								label={t('marquee:form_event_name_label')}
							/>
						}
						placeholder={t('marquee:form_event_name_placeholder')}
						type='text'
						{...register('name')}
					/>

					{/* Market + Date */}
					<div className='flex flex-wrap gap-6'>
						<div
							className={cn(
								COLUMN_WIDTH,
								'flex flex-col gap-1.5'
							)}
						>
							<Controller
								control={control}
								name='market'
								render={({ field }) => (
									<Field
										error={errorText(
											errors.market?.message
										)}
										label={t('marquee:form_market_label')}
										required
									>
										<ComboboxField
											emptyLabel={t(
												'marquee:combobox_empty'
											)}
											hasError={!!errors.market}
											loading={isLoadingMarkets}
											onChange={(value) =>
												field.onChange(value ?? '')
											}
											options={marketOptions}
											placeholder={t(
												'marquee:form_market_placeholder'
											)}
											popupContainer={pageRef}
											ref={field.ref}
											searchPlaceholder={t(
												'marquee:combobox_search_placeholder'
											)}
											value={field.value || null}
										/>
									</Field>
								)}
							/>
							{showAddLocation && (
								<Button
									className='h-auto w-fit p-0'
									onClick={() => setIsAddingLocation(true)}
									size='lg'
									type='button'
									variant='tertiaryUnderlined'
								>
									{t('marquee:form_market_add_location')}
								</Button>
							)}
						</div>

						<Controller
							control={control}
							name='date'
							render={({ field }) => (
								<Field
									className={COLUMN_WIDTH}
									error={errorText(errors.date?.message)}
									label={t('marquee:form_date_label')}
									required
								>
									<SingleDatePicker
										popupContainer={pageRef}
										setValue={(date) =>
											field.onChange(
												date
													? dayjs(date).format(
															'YYYY-MM-DD'
														)
													: ''
											)
										}
										trigger={
											<button
												className={cn(
													'flex h-11 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border bg-neutral-white px-3.5 text-base transition-all duration-150',
													errors.date
														? 'border-danger-300'
														: 'border-neutral-300'
												)}
												type='button'
											>
												<span
													className={cn(
														'truncate',
														field.value
															? 'text-neutral-900'
															: 'text-neutral-400'
													)}
												>
													{field.value
														? dayjs(
																field.value
															).format('ll')
														: t(
																'marquee:form_date_placeholder'
															)}
												</span>
												<CalendarIcon
													className='shrink-0 text-neutral-500'
													size={18}
												/>
											</button>
										}
										value={
											field.value
												? dayjs(field.value).toDate()
												: undefined
										}
									/>
								</Field>
							)}
						/>
					</div>

					{/* Starting time + Ending time */}
					<TimeRangeFields
						control={control}
						errors={errors}
						popupContainer={pageRef}
						timeZone={hintTimeZone}
					/>

					{/* Capture mode. Chosen at create; `MarqueeEventUpdate` carries
					    no `capture_mode`, so on edit it is what the event already
					    is — static text rather than a control that suggests it
					    could still be changed. */}
					{isEdit ? (
						<div className='flex flex-col gap-1.5'>
							<Label>
								{t('marquee:form_capture_mode_label')}
							</Label>
							<p className='font-medium text-base text-neutral-900'>
								{t(
									captureModeToLabelKey[
										captureModeValue ??
											MARQUEE_DEFAULT_CAPTURE_MODE
									]
								)}
							</p>
							<InputHelpText
								helpText={t(
									'marquee:form_capture_mode_locked_hint'
								)}
							/>
						</div>
					) : (
						<Controller
							control={control}
							name='captureMode'
							// The mode decides whether CTAs are required, so
							// zod reports that failure on `ctas` while the
							// field being changed is this one. RHF commits a
							// re-validation only to the field that changed, so
							// the dependent field is declared here.
							render={({ field }) => (
								<div className='flex flex-col gap-3'>
									<Label>
										{t('marquee:form_capture_mode_label')}
									</Label>
									<div className='flex flex-col gap-2'>
										{MARQUEE_CAPTURE_MODE_OPTIONS.map(
											(option) => (
												<Radio
													checked={
														field.value === option
													}
													key={option}
													label={
														<span className='flex flex-col'>
															<span className='font-medium text-base text-neutral-900'>
																{t(
																	captureModeToLabelKey[
																		option
																	]
																)}
															</span>
															<span className='text-neutral-500 text-sm'>
																{t(
																	captureModeToHintKey[
																		option
																	]
																)}
															</span>
														</span>
													}
													name={field.name}
													onBlur={field.onBlur}
													onChange={() =>
														field.onChange(option)
													}
													ref={
														field.value === option
															? field.ref
															: undefined
													}
													value={option}
												/>
											)
										)}
									</div>
								</div>
							)}
							rules={{ deps: ['ctas'] }}
						/>
					)}

					{/* Salesforce activity type — the one export cell we decide.
					    Optional: left unset, the backend defaults it. */}
					<Controller
						control={control}
						name='format'
						render={({ field }) => (
							<Field
								className={COLUMN_WIDTH}
								label={t('marquee:form_format_label')}
							>
								<Select
									onValueChange={field.onChange}
									value={field.value ?? null}
								>
									<SelectTrigger
										className='w-full'
										nativeButton={false}
									>
										{/* The empty case is handled inside the
										    render function, not by `placeholder`:
										    base-ui ignores that prop whenever
										    children is a function. The trigger
										    still carries `data-placeholder`, so
										    the text stays grey. */}
										<SelectValue>
											{(
												value: MarqueeEventFormat | null
											) =>
												value
													? eventFormatToLabel[value]
													: t(
															'marquee:form_format_placeholder'
														)
											}
										</SelectValue>
									</SelectTrigger>
									<SelectContent
										className='min-w-(--anchor-width)'
										container={pageRef}
									>
										{MARQUEE_EVENT_FORMAT_OPTIONS.map(
											(option) => (
												<SelectItem
													key={option}
													value={option}
												>
													{eventFormatToLabel[option]}
												</SelectItem>
											)
										)}
									</SelectContent>
								</Select>
							</Field>
						)}
					/>

					{/* Event type. Two options only: `null` ("not recorded", on
					    events created before the field existed) is rendered as a
					    hint, never offered as a third choice. */}
					<Controller
						control={control}
						name='eventType'
						render={({ field }) => (
							<div className='flex flex-col gap-3'>
								<Label>
									{t('marquee:form_event_type_label')}
								</Label>
								{field.value === null && (
									<InputHelpText
										helpText={t(
											'marquee:form_event_type_unrecorded_hint'
										)}
									/>
								)}
								<div className='flex flex-col gap-3'>
									{MARQUEE_EVENT_TYPE_OPTIONS.map(
										(option) => (
											<Radio
												checked={field.value === option}
												key={option}
												label={t(
													eventTypeToLabelKey[option]
												)}
												name={field.name}
												onBlur={field.onBlur}
												onChange={() =>
													field.onChange(option)
												}
												ref={
													field.value === option
														? field.ref
														: undefined
												}
												value={option}
											/>
										)
									)}
								</div>
							</div>
						)}
					/>

					{/* Campaign hashtag */}
					<InputText
						errorMessage={errorText(errors.hashtag?.message)}
						helpText={t('marquee:form_hashtag_hint')}
						label={
							<RequiredLabel
								label={t('marquee:form_hashtag_label')}
							/>
						}
						placeholder={t('marquee:form_hashtag_placeholder')}
						type='text'
						{...hashtagField}
						onBlur={handleHashtagBlur}
					/>

					{/* Blanket CTAs. Required on a presentation, optional on a
					    tune-up, which captures per attendee as well. */}
					<EventFormCtasSection
						control={control}
						errors={errors}
						popupContainer={pageRef}
						required={requiresCtas}
					/>

					{/* Assigned Market Managers */}
					<Controller
						control={control}
						name='marketManagerIds'
						render={({ field }) => (
							<div className='flex flex-col gap-2'>
								<Label htmlFor='marketManagerIds'>
									<RequiredLabel
										label={t(
											'marquee:managers_section_title'
										)}
									/>
								</Label>
								<ManagerMultiSelect
									emptyLabel={t('marquee:combobox_empty')}
									hasError={!!errors.marketManagerIds}
									id='marketManagerIds'
									loading={isLoadingManagers}
									onChange={field.onChange}
									options={managerOptions}
									placeholder={t(
										'marquee:managers_select_placeholder'
									)}
									popupContainer={pageRef}
									ref={field.ref}
									removeLabel={(name) =>
										t('marquee:managers_remove', { name })
									}
									searchPlaceholder={t(
										'marquee:combobox_search_placeholder'
									)}
									value={field.value ?? []}
								/>
								<InputHelpText
									error={errorText(
										errors.marketManagerIds?.message
									)}
								/>
							</div>
						)}
					/>

					{/* Invitee list — CSV import runs only on create; on edit the
					    invitee set is managed from the Attendees tab. */}
					{!isEdit && (
						<EventFormInviteeSection
							error={inviteeError}
							file={inviteeFile}
							isCreating={create.isPending}
							isSubmitting={isSubmitting}
							onFileChange={handleInviteeChange}
						/>
					)}

					{/* Footer buttons */}
					<div className='flex items-center justify-between border-neutral-200 border-t pt-6'>
						<Button
							onClick={() =>
								navigate(routes.MARQUEE_EVENTS.handle.fullPath)
							}
							type='button'
							variant='tertiary'
						>
							{t('marquee:cancel')}
						</Button>
						<Button
							disabled={isSubmitting}
							type='submit'
							variant='primary'
						>
							{t(
								isEdit
									? 'marquee:submit_edit'
									: 'marquee:submit_create'
							)}
							{isSubmitting && <Spinner size={16} />}
						</Button>
					</div>
				</div>

				{/* Right column on desktop; on mobile it moves above the form
				    fields (between the page title and the event name). */}
				<EventFormInfoCard />
			</form>

			{/* Outside the form: a dialog's events bubble through the portal. The
			    new place is what the admin came for, so it is picked for them. */}
			{isAddingLocation && (
				<LocationDialog
					onClose={() => setIsAddingLocation(false)}
					onCreated={(location) =>
						setValue(
							'market',
							eventPlaceValue({
								kind: 'location',
								id: location.id
							}),
							{ shouldDirty: true, shouldValidate: true }
						)
					}
					target={{ mode: 'create' }}
				/>
			)}
		</div>
	)
}

export { MarqueeEventForm }
